import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/deploy/components/ui/Modal';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import type { NetworkJob } from '@/deploy/types/network';
import { JOB_STATUS, errorMessage } from './networkFormat';
import styles from './Network.module.scss';

interface JobModalProps {
  /** Job renvoyé par l'action (202). */
  job: NetworkJob;
  title: string;
  /** Lecture du job (route projet ou route admin). */
  fetchJob: (id: number) => Promise<NetworkJob>;
  onClose: () => void;
  /** Appelé une fois quand le job se termine (succès ou échec), pour rafraîchir la page. */
  onDone?: (job: NetworkJob) => void;
}

/** Suivi d'une opération ssl-manager : statut et log, rafraîchis jusqu'à la fin. */
export function JobModal({ job: initial, title, fetchJob, onClose, onDone }: JobModalProps) {
  const [job, setJob] = useState<NetworkJob>(initial);
  const [pollError, setPollError] = useState<string | null>(null);
  const logRef = useRef<HTMLPreElement>(null);
  const doneRef = useRef(false);
  const finished = job.status === 'SUCCESS' || job.status === 'FAILED';

  useEffect(() => {
    if (finished) return;
    const t = setTimeout(async () => {
      try {
        setJob(await fetchJob(initial.id));
        setPollError(null);
      } catch (e) {
        setPollError(errorMessage(e));
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [job, finished, fetchJob, initial.id]);

  useEffect(() => {
    if (finished && !doneRef.current) {
      doneRef.current = true;
      onDone?.(job);
    }
  }, [finished, job, onDone]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [job.log]);

  const status = JOB_STATUS[job.status];
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={job.subject ?? undefined}
      width="lg"
      footer={<Button variant={finished ? 'primary' : 'ghost'} onClick={onClose}>{finished ? 'Fermer' : 'Continuer en arrière-plan'}</Button>}
    >
      <div className={styles.form}>
        <div className={styles.jobHead}>
          <Badge tone={status.tone} dot>{status.label}</Badge>
          <span className={styles.muted}>Job #{job.id}</span>
        </div>
        {job.status === 'FAILED' && job.error && <div className={styles.error}>{job.error}</div>}
        {pollError && <div className={styles.error}>Suivi interrompu : {pollError}</div>}
        <pre ref={logRef} className={styles.code}>{job.log || (finished ? '(pas de log)' : 'En attente du démarrage…')}</pre>
      </div>
    </Modal>
  );
}
